import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Eye } from 'lucide-react' 
import { toast } from 'react-toastify';
import { useLoading } from '../../contexts/LoadingContext';
import { useLoginUser } from '../../hooks/db/useLogin';
import { useGetConversations } from '../../hooks/db/useGetConversations';
import { formatDateTime, getConversationTitle } from '../../lib/common';
import PaginationControls from '../PaginationControls/PaginationControls';

const Conversations: React.FC = () => {
    const { getConversations } = useGetConversations()
    const navigate = useNavigate()
    const [conversations, setConversations] = useState<Record<string, any>[]>([])
    const { forgotPasswordUser } = useLoginUser()
    const { setLoading } = useLoading()
    const [selectedConversation, setSelectedConversation] = useState<Record<string, any> | null>(null)

    const [totalCount, setTotalCount] = useState(0)
    const pageSize = parseInt(process.env.REACT_APP_PAGE_SIZE || '10', 10)

    const [searchParams, setSearchParams] = useSearchParams()
    const initialPage = parseInt(searchParams.get('page') || '1', 10)
    const [currentPage, setCurrentPage] = useState(initialPage)

    const handleView = (conversation: any) => {
        setSelectedConversation(conversation)
    }

    const fetchConversations = async (page: number) => {
        setLoading(true)
        try {
            let filters = {}
            const result = await getConversations(page, pageSize, filters)
            if (result && 'conversations' in result) {
                setConversations((result as { conversations: any }).conversations)
                setTotalCount(result.totalCount)
            }
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log("Fetch aborted");
            } else {
                console.error("Error fetching conversations:", error);
            }
                toast.error("Error fetching conversations", {
                position: "bottom-center",
                })
        }finally{
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchConversations(currentPage)
    }, [currentPage])


    return (
        <div className="flex flex-col md:flex-row h-[80vh] overflow-hidden">
        {/* Left panel - Table */}
        {!selectedConversation || window.innerWidth >= 768 ? (
            <div className={`overflow-auto ${selectedConversation ? 'md:w-1/2' : 'w-full'} `}>
            <div className="flex justify-between items-center p-4">
                <h2 className="text-xl font-bold">Conversations</h2>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg border text-sm text-left">
                <thead className="bg-gray-200">
                    <tr>
                    <th className="px-4 py-2 border-b text-gray-700">Title</th>
                    <th className="px-4 py-2 border-b text-gray-700">User</th>
                    <th className="px-4 py-2 border-b text-gray-700">Created At</th>
                    <th className="px-4 py-2 border-b text-gray-700">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {conversations.map((row, index) => (
                    <tr
                        key={index}
                        className={`hover:bg-gray-50 cursor-pointer ${selectedConversation?._id === row._id ? 'bg-white border-2' : ''}`}
                    >
                        <td
                        className="px-4 py-2 border-b text-blue-600 hover:underline"
                        onClick={() => handleView(row)}
                        width="50%"
                        >
                        {getConversationTitle(row)}
                        </td>
                        <td className="px-4 py-2 border-b">{row.user?.name || 'N/A'}</td>
                        <td className="px-4 py-2 border-b">
                            {formatDateTime(row.createdAt)}
                        </td>
                        <td className="px-4 py-2 border-b">
                            <div className="flex space-x-2">
                                <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleView(row)}
                                title="View Conversation"
                                >
                                <Eye className="h-4 w-4 mr-1" />
                                </Button>
                            </div>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>
            <PaginationControls
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(newPage) => {
                    setCurrentPage(newPage)
                    setSearchParams({ page: newPage.toString() })
                }}
            />
            </div>
        ) : null}

        {/* Right panel - Messages */}
        {selectedConversation && (
            <div className="w-full md:w-1/2 p-4 overflow-auto  border rounded-lg flex flex-col">
            {/* Back button on small screens */}
            <div className="md:hidden mb-2">
                <button
                className="text-blue-600 text-sm underline"
                onClick={() => setSelectedConversation(null)}
                >
                ← Back to conversations
                </button>
            </div>

            <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold capitalize">
                {getConversationTitle(selectedConversation)}
                </h3>
                <button
                className=" button text-sm text-gray-500 text-xl hover:underline hidden md:inline font-bold"
                onClick={() => setSelectedConversation(null)}
                title='Close Conversation'
                >
                <svg focusable="false" width="24" height="24" viewBox="0 0 24 24" >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
                </svg>
                </button>
            </div>

            <div className="space-y-2 overflow-auto text-sm">
                {selectedConversation.messages?.length ? (
                selectedConversation.messages.map(
                    (
                    msg: { sender: string; content: string; createdAt: Date },
                    i: React.Key | null | undefined
                    ) => (
                    <div
                        key={i}
                        className={`conv-message-item px-4 py-4 rounded-lg flex flex-col ${
                        msg.sender === 'user'
                            ? 'bg-blue-100 self-end text-right'
                            : 'bg-gray-200 self-start text-left'
                        }`}
                    >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <span className="mt-1 text-xs text-gray-500">
                        {/* {new Date(msg.createdAt).toLocaleString()} */}
                        </span>
                    </div>
                    )
                )
                ) : (
                <p className="text-gray-500">No messages available.</p>
                )}
            </div>
            </div>
        )}
        </div>

      )
      
}

export default Conversations